import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    BooleanModel,
    ConfigConstants,
    DisplayDecorator,
    DropdownModel,
    TextModel,
    TextModelOptions,
    SmartlockerDeliveryStatus,
} from '@evorait/pace-core';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { catchError, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Delivery } from '../../interfaces/smartlocker-deliveries.interface';
import { SmartlockerService } from '../../services/smartlocker.service';

@Component({
    selector: 'pace-smartlocker',
    templateUrl: './smartlocker.component.html',
    styleUrls: ['./smartlocker.component.scss'],
    standalone: false,
})
export class SmartlockerComponent implements OnDestroy, AfterViewInit {
    public smartlockerDeliveries$: Observable<Delivery[]>;
    private searchFilterSubject = new BehaviorSubject<string>('');
    private showDeliveriesOnlySubject = new BehaviorSubject<boolean>(false);
    private destroy$ = new Subject<void>();
    private preferredSmartlockerFilterSubject = new BehaviorSubject<void>(undefined);
    public deliveries$: Observable<Delivery[]>;
    public smartlockers: DropdownModel<TextModel>;
    public searchFilterModel: TextModel;
    public showDeliveriesOnly: DisplayDecorator<BooleanModel>;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly smartlockerService: SmartlockerService,
        private readonly cdr: ChangeDetectorRef,
        private appNotificationService: AppNotificationService,
        private translate: TranslateService,
    ) {
        this.searchFilterModel = new TextModel(
            'Search',
            '',
            new TextModelOptions({ placeholder: 'Search by AWB or Smartlocker' }),
        );

        this.showDeliveriesOnly = new DisplayDecorator<BooleanModel>(
            'Show Delivered only',
            new BooleanModel('', false),
        );

        this.deliveries$ = this.smartlockerService.smartlockerDeliveries$.pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('SMARTLOCKER_GET_DELIVERIES_ERROR_TITLE'),
                    `${<string>this.translate.instant('SMARTLOCKER_GET_DELIVERIES_ERROR_BODY')}`,
                );
                this.log.error(
                    `SmartLockerComponent: deliveries$: Error while retrieving deliveries: ${JSON.stringify(error)}`,
                );
                return of([]);
            }),
        );

        this.smartlockerDeliveries$ = combineLatest([
            this.deliveries$,
            this.searchFilterSubject.asObservable().pipe(startWith(''), debounceTime(ConfigConstants.debounceTimeMs)),
            this.preferredSmartlockerFilterSubject
                .asObservable()
                .pipe(switchMap(() => this.smartlockerService.getPreferredSmartlocker())),
            this.showDeliveriesOnlySubject.asObservable(),
        ]).pipe(
            map(([deliveries, textFilter, lockerFilter, showDeliveriesOnly]) => {
                let filteredDeliveries = deliveries;

                // Filter first to the preferred locker
                if (lockerFilter) {
                    const targetLocker = parseInt(lockerFilter, 10);
                    filteredDeliveries = filteredDeliveries.filter(
                        (delivery) => delivery.SmartlockerId === targetLocker,
                    );
                }

                // Search only within the already filtered subset
                if (textFilter) {
                    const lowFilter = textFilter.toLowerCase().trim();
                    filteredDeliveries = filteredDeliveries.filter(
                        (delivery) =>
                            delivery.AWB.toLowerCase().includes(lowFilter) ||
                            delivery.SmartlockerIdentifier.toLowerCase().includes(lowFilter),
                    );
                }

                if (showDeliveriesOnly) {
                    filteredDeliveries = filteredDeliveries.filter(
                        (delivery) => delivery.Status === SmartlockerDeliveryStatus.Delivered,
                    );
                }

                return filteredDeliveries;
            }),
        );

        this.smartlockers = new DropdownModel<TextModel>('Smartlockers', []);

        this.smartlockerService
            .getSmartlockers()
            .pipe(take(1), withLatestFrom(this.smartlockerService.getPreferredSmartlocker()))
            .subscribe(([lockers, preferredSmartlocker]) => {
                this.smartlockers.value = lockers.map(
                    (sl) =>
                        new DisplayDecorator<TextModel>(
                            `${sl.Identifier} ${sl.City} ${sl.Street}`,
                            new TextModel('Smartlockers', sl.SmartlockerId.toString()),
                        ),
                );

                if (preferredSmartlocker.length > 0) {
                    this.smartlockers.selectByValue(preferredSmartlocker);
                }
            });
    }

    public ngAfterViewInit(): void {
        this.smartlockerService.refreshSmartlockerDeliveriesSubject.next();
    }

    public onSearchFilterChange(event: KeyboardEvent): void {
        const value = (event.target as HTMLInputElement).value?.toLowerCase().trim() ?? '';
        this.searchFilterSubject.next(value);
    }

    public onPreferredSmartlockerChange(): void {
        const smartlocker = this.smartlockers.selectedEntry.model.value;

        this.smartlockerService
            .savePreferredSmartlocker(smartlocker)
            .pipe(take(1))
            .subscribe(() => this.preferredSmartlockerFilterSubject.next());
    }

    public onShowDeliveriesOnlyChange(): void {
        this.showDeliveriesOnlySubject.next(this.showDeliveriesOnly.value);
    }

    public clearFilter(): void {
        this.searchFilterSubject.next('');
        this.searchFilterModel = {
            ...this.searchFilterModel,
            value: '',
        } as TextModel;

        this.cdr.detectChanges();
    }

    public clearPreferredSmartlocker(): void {
        this.smartlockers.deselect();
        this.smartlockerService
            .savePreferredSmartlocker('')
            .pipe(take(1))
            .subscribe(() => this.preferredSmartlockerFilterSubject.next());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
